import api from './api'
import type { KillReason, Loan, LoanKillInput, LoanKillRecord, PipelineMetrics } from '@/types'
import type {
  LoanActivityEvent,
  LoanFieldHistoryEvent,
  LoanFieldLineageResponse,
  LoanProvenance,
} from '@/pages/pipeline-intelligence/types'
import { toCamelCase } from '@/utils/transform'
import { ifMatch } from './dealFile'

/**
 * The API serves an unstated loan amount as null (the NOT NULL column's 0.0
 * placeholder is never a value a client reads). Inside the app `Loan.loanAmount`
 * keeps its numeric contract, where 0 is the "not stated" marker every screen
 * already renders as "—" — so a null never reaches arithmetic or `toFixed`.
 */
export function withUnstatedAmount<T extends { loanAmount?: number | null }>(loan: T): T {
  if (!loan) return loan
  return loan.loanAmount === null || loan.loanAmount === undefined ? { ...loan, loanAmount: 0 } : loan
}

/**
 * GET /loans serves a dead deal's kill timestamp under the column's own name,
 * `deadAt`; only the kill and reopen responses say `killedAt`. Every screen reads
 * `killedAt` (the kill panel, the dossier), so a dead row opened from a list
 * looked like a live one — the panel offered to KILL a deal that was already
 * dead. Mirrored once, here, so no caller has to know there are two names. The
 * rest of the record (`deadReason`, `killedAtStage`, `killReasonCode`, ...) is
 * already served under the names the app reads.
 */
export function withKillRecord<T extends { deadAt?: string | null; killedAt?: string | null }>(loan: T): T {
  if (!loan || loan.killedAt || !loan.deadAt) return loan
  return { ...loan, killedAt: loan.deadAt }
}

/** Every normalisation a loan read gets on its way into the app. */
function normaliseLoan<T extends { loanAmount?: number | null; deadAt?: string | null; killedAt?: string | null }>(
  loan: T,
): T {
  return withKillRecord(withUnstatedAmount(loan))
}

/** The activity API's largest page, and a ceiling on pages read for one deal. */
const ACTIVITY_PAGE_SIZE = 200
const ACTIVITY_MAX_PAGES = 25

/** How a deal's numbers were arrived at, and the per-deal overrides in force
 *  (register Q4/Q5/Q6). The SERVER decides what the reader may see: the derived
 *  figures, their formulas and inputs, the variances and the one agreement
 *  tolerance go to EVERY reader of the deal (UI-03), so no figure depends on
 *  role; the override surface (`overrides`, `overridden`, `conflicts`,
 *  `componentValuation`, `audit`) arrives only with the unlock privilege, and
 *  an `unlocked: false` reader is given none of it — there is nothing to render
 *  a disabled control over, and no client-side permission rule to drift. */
export interface DealCalculations {
  loanId: string
  unlocked: boolean
  /** A stated date the closing date and the term do not reproduce, keyed by
   *  the camel-cased field (`maturityDate`), for information (DER-11, R4). */
  variances?: Record<string, { stated: string; computed: string; note: string; informational?: boolean }>
  /** A rule the deal's structure makes meaningless, with the reason (DER-11). */
  withheld?: Record<string, string>
  /** The one relative tolerance every stage and the browser compare against. */
  agreementTolerance?: number
  derived?: Record<string, {
    value: number
    formula: string | null
    inputs: Record<string, number | null>
    recomputedNow: boolean
    matchesInputs: boolean
    fromInputs: number | null
  }>
  conflicts?: Record<string, { computed: number; stated: number; note: string }>
  overridden?: Record<string, { value: number; engine: number | null }>
  overrides?: {
    id: string
    target: string
    value: number
    engineValue: number | null
    reason: string
    createdBy: string
    createdAt: string
  }[]
  componentValuation?: {
    value: number
    noi: number
    formula: string
    components: ValuationComponent[]
  } | null
  audit?: CalculationAuditEvent[]
}

export interface ValuationComponent {
  id: string
  name: string
  assetType: string | null
  noi: number
  capRate: number
  value: number
  formula: string
  createdBy: string
  createdAt: string
  updatedBy: string | null
  updatedAt: string | null
}

export interface CalculationAuditEvent {
  id: string
  subjectType: 'override' | 'valuation_component'
  subjectId: string
  eventType: string
  target: string | null
  detail: Record<string, unknown>
  reason: string
  actor: string
  createdAt: string
  prevHash: string
  eventHash: string
}

export interface ValuationComponentInput {
  name: string
  assetType?: string | null
  noi: number
  capRate: number
  reason: string
}

export interface ValuationComponentsResponse {
  loanId: string
  components: ValuationComponent[]
  combinedValuation: DealCalculations['componentValuation']
}

/** An open deal a hand-entered deal may already be (stress test CMP-05). */
export interface PossibleDuplicateDeal {
  loanId: string
  /** `property_name` arrives as `property` through toCamelCase. */
  property?: string | null
  location?: string | null
  sponsor?: string | null
  loanAmount?: number | null
  basis: string
  reason: string
  score: number
}

/** Why a pipeline cell is blank — or why a filled one is in question — per
 *  deal and per field. `revision_pending` (a later email's figure waiting on a
 *  person) and `data_conflict` (the sweep's arithmetic disagrees) mark FILLED
 *  cells; they carry the intake record to open. */
export type PipelineReviewGaps = {
  gaps: Record<string, Record<string, {
    status: string
    candidates: number
    ingestionId?: string | null
    receivedAt?: string | null
  }>>
  openStatuses: string[]
  basis?: string
}

export const loansService = {
  /**
   * A blank cell has three meanings — rival statements nobody has chosen
   * between, a value no document states, or a calculation awaiting
   * confirmation — and the grid could show none of them. Deliberately carries
   * the count of rival statements, never the statements: choosing between them
   * is the Data Intelligence Review's job, with the evidence attached.
   */
  getReviewGaps: () =>
    api.get<PipelineReviewGaps>('/loans/review-gaps').then((r) => r.data),

  /** Open deals a deal about to be entered by hand may already be — the same
   *  identity evidence intake promotion uses (stress test CMP-05). */
  possibleDuplicates: (params: {
    property_name?: string
    street?: string
    city?: string
    state?: string
    location?: string
    sponsor?: string
  }) =>
    api
      .get('/loans/possible-duplicates', { params })
      .then((r) => toCamelCase<{ candidates: PossibleDuplicateDeal[]; basis: string }>(r.data)),

  getCalculations: (loanId: string) =>
    api.get(`/loans/${loanId}/calculations`).then((r) => toCamelCase<DealCalculations>(r.data)),

  setCalculationOverride: (loanId: string, target: string, value: number, reason: string) =>
    api.post(`/loans/${loanId}/calculation-overrides`, { target, value, reason })
      .then((r) => r.data),

  withdrawCalculationOverride: (loanId: string, overrideId: string, reason: string) =>
    api.delete(`/loans/${loanId}/calculation-overrides/${overrideId}`, { data: { reason } }).then((r) => r.data),

  createValuationComponent: (loanId: string, input: ValuationComponentInput) =>
    api.post(`/loans/${loanId}/valuation-components`, {
      name: input.name,
      asset_type: input.assetType,
      noi: input.noi,
      cap_rate: input.capRate,
      reason: input.reason,
    }).then((r) => toCamelCase<ValuationComponentsResponse>(r.data)),

  updateValuationComponent: (loanId: string, componentId: string, input: ValuationComponentInput) =>
    api.put(`/loans/${loanId}/valuation-components/${componentId}`, {
      name: input.name,
      asset_type: input.assetType,
      noi: input.noi,
      cap_rate: input.capRate,
      reason: input.reason,
    }).then((r) => toCamelCase<ValuationComponentsResponse>(r.data)),

  withdrawValuationComponent: (loanId: string, componentId: string, reason: string) =>
    api.delete(`/loans/${loanId}/valuation-components/${componentId}`, { data: { reason } })
      .then((r) => toCamelCase<ValuationComponentsResponse>(r.data)),

  getAll: (params?: Record<string, string>) =>
    // Trailing slash matches FastAPI's `/loans/` route. `/loans` 307s; a
    // browser following that redirect can drop the bearer or fail CORS on
    // the bounce, which is how Underwriting/Surveillance stuck in isError
    // while Pipeline looked empty.
    api.get('/loans/', { params }).then((r) => toCamelCase<Loan[]>(r.data).map(normaliseLoan)),

  getById: (id: string) =>
    api.get(`/loans/${id}`).then((r) => normaliseLoan(toCamelCase<Loan>(r.data))),

  create: (data: Partial<Loan>) =>
    api.post('/loans', data).then((r) => normaliseLoan(toCamelCase<Loan>(r.data))),

  /** `version` (the loan's updatedAt when loaded) is sent as If-Match so a
   *  save from an out-of-date view is refused rather than overwriting. It is
   *  required: the server refuses a save that names no version (428). */
  update: (id: string, data: Partial<Loan>, version: string | null) =>
    api.put(`/loans/${id}`, data, ifMatch(version)).then((r) => normaliseLoan(toCamelCase<Loan>(r.data))),

  /** Append a note to the deal file (append-only, stamped with author + time). */
  addNote: (id: string, body: string) =>
    api.post(`/loans/${id}/notes`, { body }).then((r) =>
      toCamelCase<{ id: string; body: string; author: string; createdAt: string }>(r.data),
    ),

  /** Write underwriting narratives (fixed section vocabulary; empty text
   *  clears a section; every write is audited server-side). */
  updateNarratives: (id: string, sections: Record<string, string>) =>
    api.put(`/loans/${id}/narratives`, { sections }).then((r) =>
      toCamelCase<{ id: string; narratives: Record<string, { text: string; updatedBy: string; updatedAt: string }> }>(r.data),
    ),

  /** Append a timestamped note to a named narrative section. */
  appendNarrativeEntry: (
    id: string,
    payload: {
      section: string
      text: string
      attachment?: { documentId: string; filename: string }
    },
  ) =>
    api.post(`/loans/${id}/narratives/entries`, {
      section: payload.section,
      text: payload.text,
      attachment: payload.attachment
        ? {
            document_id: payload.attachment.documentId,
            filename: payload.attachment.filename,
          }
        : undefined,
    }).then((r) =>
      toCamelCase<{
        id: string
        narratives: Record<string, unknown>
        entry: {
          id: string
          text: string
          author: string
          createdAt: string
          attachment?: { documentId: string; filename: string }
        }
      }>(r.data),
    ),

  listNotes: (id: string) =>
    api.get(`/loans/${id}/notes`).then((r) =>
      toCamelCase<{ id: string; body: string; author: string; createdAt: string }[]>(r.data),
    ),

  getProvenance: (id: string) =>
    api.get(`/loans/${id}/provenance`).then((r) => toCamelCase<LoanProvenance>(r.data)),

  getFieldLineage: (id: string) =>
    api.get(`/loans/${id}/field-lineage`).then((r) => {
      const data = toCamelCase<LoanFieldLineageResponse>(r.data)
      return {
        ...data,
        fields: (data.fields ?? []).map((field) => {
          const fieldName = field.fieldName || field.field || ''
          return {
            ...field,
            fieldName,
            currentValue: field.currentValue !== undefined ? field.currentValue : field.value,
            updatedAt: field.updatedAt ?? field.createdAt ?? null,
            history: (field.history ?? []).map((event) => ({
              ...event,
              fieldName: event.fieldName || fieldName,
            })),
          }
        }),
      }
    }),

  getFieldHistory: (id: string, field?: string) =>
    api
      .get(`/loans/${id}/field-history`, { params: field ? { field } : undefined })
      .then((r) => {
        const payload = toCamelCase<{ loanId?: string; events?: LoanFieldHistoryEvent[] } | LoanFieldHistoryEvent[]>(
          r.data,
        )
        if (Array.isArray(payload)) return payload
        return payload.events ?? []
      }),

  /** What saving these edits would derive, computed by the engine the save
   *  runs (rule set and scale are the server's) — writes nothing. Keys are
   *  catalogue field names, so the body is read raw, not camel-cased. */
  previewDerivation: (id: string, draft: Record<string, unknown>) =>
    api
      .post(`/loans/${id}/derivation/preview`, { draft })
      .then((r) => r.data as {
        derived: Record<string, number>
        notes: Record<string, string>
        variances: Record<string, { computed: number; stated: number; note: string }>
        refused?: Record<string, unknown>
      }),

  approveField: (id: string, fieldName: string, reason: string) =>
    api
      .post(`/loans/${id}/fields/${encodeURIComponent(fieldName)}/approve`, { reason })
      .then((r) => toCamelCase<LoanFieldHistoryEvent>(r.data)),

  /**
   * Every activity event on the deal, newest first, read a page at a time.
   *
   * One unparameterised call returned the API's default fifty, and the Audit
   * tab counted those fifty as every event on the deal (stress test DJA-12).
   * An event seen on two pages — one written between the reads shifts the
   * offsets — is kept once.
   */
  getActivity: async (id: string): Promise<LoanActivityEvent[]> => {
    const events: LoanActivityEvent[] = []
    const seen = new Set<string>()
    let offset = 0
    for (let page = 0; page < ACTIVITY_MAX_PAGES; page += 1) {
      const r = await api.get(`/loans/${id}/activity`, { params: { limit: ACTIVITY_PAGE_SIZE, offset } })
      const payload = toCamelCase<{
        loanId: string
        events: LoanActivityEvent[]
        hasMore?: boolean
        nextOffset?: number
      }>(r.data)
      for (const event of payload.events ?? []) {
        const key = String(event.id)
        if (seen.has(key)) continue
        seen.add(key)
        events.push(event)
      }
      if (!payload.hasMore || typeof payload.nextOffset !== 'number' || payload.nextOffset <= offset) break
      offset = payload.nextOffset
    }
    return events
  },

  delete: (id: string) =>
    api.delete(`/loans/${id}`),

  /** The kill-reason picklist. Codes in `requireNote` are not an explanation on
   *  their own — the UI must collect a note before allowing the kill.
   *  `contactMethods` are the allowed values for the broker-contact attestation. */
  getKillReasons: () =>
    api.get('/loans/kill-reasons').then((r) =>
      toCamelCase<{ reasons: KillReason[]; requireNote: string[]; contactMethods: string[] }>(r.data),
    ),

  /** Mark a deal dead. Retained, not deleted: the reason, the note, and the
   *  stage it died at are recorded and stay searchable. The F4/F5 broker-contact
   *  attestation is required — `brokerContacted` is a mandatory yes/no, and a
   *  "yes" must carry the method (call|email) and the ISO date of contact. */
  kill: (id: string, input: LoanKillInput) =>
    api
      .post(`/loans/${id}/kill`, {
        reason_code: input.reasonCode,
        note: input.note || null,
        broker_contacted: input.brokerContacted,
        contact_method: input.brokerContacted ? input.contactMethod : null,
        contact_date: input.brokerContacted ? input.contactDate : null,
      })
      .then((r) => toCamelCase<LoanKillRecord & { id: string }>(r.data)),

  /** Return a killed deal to the working pipeline. The kill history survives in
   *  the append-only field-event trail. */
  reopen: (id: string, reason: string) =>
    api
      .post(`/loans/${id}/reopen`, { reason })
      .then((r) => toCamelCase<LoanKillRecord & { id: string }>(r.data)),

  getMetrics: () =>
    api.get<PipelineMetrics>('/loans/metrics').then((r) => r.data),

  getModelComparison: (id: string) =>
    api.get(`/loans/${id}/model-comparison`).then((r) => toCamelCase(r.data)),

  /** Register B3 — stage-by-stage dwell history for the dossier timeline.
   *  Reads the same append-only lineage as the grid's Days in Status, so the
   *  timeline and the column can never disagree. */
  getStageTimeline: (id: string) =>
    api.get(`/loans/${id}/stage-timeline`).then((r) => toCamelCase<StageTimeline>(r.data)),

  /** Register F3 — the litigation record: every recorded event grouped by
   *  the stage segment open at the time. Served raw (snake_case) on purpose:
   *  entry.detail carries arbitrary field names that must not be re-cased. */
  getStageAudit: (id: string) =>
    api.get(`/loans/${id}/stage-audit`).then((r) => r.data as StageAudit),

  /** The Field Map tab: what the deal holds, how it got there, what is missing
   *  and who has to move. `search=false` skips opening the stored package, so
   *  every blank reads "not searched" rather than "not in the package". */
  getFieldMap: (id: string, search = true) =>
    api.get(`/loans/${id}/field-map`, { params: { search } }).then((r) => toCamelCase<FieldMap>(r.data)),
}

/** Who has to move before a blank field fills. `us` is the engine's own defect. */
export type FieldMapOwner = 'us' | 'reviewer' | 'inputs' | 'broker' | 'desk'

export type FieldMapResolution =
  | 'value_available'
  | 'label_seen_unparsed'
  | 'no_search_route'
  | 'needs_review'
  | 'derivable_when'
  | 'absent_from_package'
  | 'desk_assigned'
  | 'not_applicable'
  | 'no_package'
  | 'unavailable'

export type FieldMapScore = { populated: number; total: number; pct: number }

export type FieldMapBlock = { target: string; label: string; formula: string; alsoNeeds: string[] }

export type FieldMapRow = {
  field: string
  label: string
  page: string
  group: string
  kind: string
  gate: boolean
  deskSet: boolean
  state: 'populated' | 'blank'
  /** Populated: where the dossier holds it. */
  heldOn?: 'deal' | 'deal and record' | 'intake record'
  /** Populated: how it got its value, from the field events. Absent when the
   *  platform recorded no event for it. */
  origin?: {
    state: string
    actor: 'package' | 'calculated' | 'person'
    source: string | null
    at: string | null
    engineVersion: string | null
  } | null
  /** Stored only as the platform's own default (a stack position nobody
   *  stated): not data, so the row is blank and says what the default is. */
  assumed?: { value: unknown; basis: string }
  /** A covenant term: the desk's to set, seeded by the sponsor's first reading. */
  deskOwned?: boolean
  ownership?: {
    owner: 'desk'
    seed: 'sponsor' | 'desk' | null
    stamp: string | null
    awaitingDeskTerms: boolean
    note: string | null
  }
  /** Blank only. */
  resolution?: FieldMapResolution
  owner?: FieldMapOwner | null
  why?: string | null
  need?: string
  waitingOn?: string[]
  conflict?: boolean
  found?: { value: unknown; document: string | null; sheet: string | null; cell: string | null; line: number | null; page: number | null }[]
  searched?: { document: string | null; outcome: string }[]
  impact?: {
    requiredAtIntake: boolean
    pipelineCritical: boolean
    creditScreenInput: boolean
    reviewDatapoint: boolean
    blocks: FieldMapBlock[]
  }
}

export type FieldMap = {
  loanId: string
  ingestionId: string | null
  hasPackage: boolean
  searched: boolean
  searchError: string | null
  sourcesUnreadable: number
  findingsWithheld: boolean
  basis: string
  score: {
    overall: FieldMapScore
    package: FieldMapScore
    gate: FieldMapScore & { missing: string[] }
    byPage: ({ page: string } & FieldMapScore)[]
    engineMisses: number
  }
  fields: FieldMapRow[]
  worklist: { field: string; label: string; page: string; owner: FieldMapOwner; need: string; gate: boolean; blocks: string[] }[]
  extractions: { id: string; receivedAt: string | null; attachments: number; fieldsRead: number; isSourceRecord: boolean }[]
  population: { date: string; populated: number; package: number; calculated: number; person: number }[]
}

export type StageAuditEntry = {
  at: string
  kind: 'stage_change' | 'field_edit' | 'note' | 'activity' | 'email_received' | 'created' | string
  summary: string
  /** "not recorded" when the platform never captured who — never guessed. */
  actor: string
  detail: Record<string, unknown>
}

export type StageAuditSegment = {
  index: number
  stage: string
  entered_at: string
  exited_at: string | null
  days: number
  current: boolean
  event_count: number
  events: StageAuditEntry[]
}

export type StageAudit = {
  loan_id: string
  segments: StageAuditSegment[]
  total_events: number
  unplaced_events: StageAuditEntry[]
  /** Every entry, oldest first — creation, arrival, field changes, parties,
   *  documents, checklist, notes. */
  entries?: StageAuditEntry[]
  /** When the record begins: the package's arrival where there was one. */
  first_event_at?: string | null
  /** Sources the register asks for that the platform does not capture. */
  uncaptured_sources: { source: string; detail: string }[]
}

/** One continuous occupancy of a stage. A deal that moves backward produces a
 *  second segment for the re-entered stage rather than merging into the first —
 *  the desk needs "how long this time". */
export type StageDwellSegment = {
  stage: string
  enteredAt: string
  exitedAt: string | null
  days: number
  current: boolean
}

export type StageTimeline = {
  loanId: string
  currentStage: string
  daysInStatus: number | null
  totalDaysOpen: number | null
  segments: StageDwellSegment[]
}

export const triggersService = {
  getAll: () => api.get('/triggers').then((r) => toCamelCase(r.data)),
  resolve: (id: string) => api.patch(`/triggers/${id}/resolve`).then((r) => r.data),
}

export const reportsService = {
  generate: (params: {
    template: string
    filters?: Record<string, unknown>
    format?: 'pdf' | 'xlsx'
    /** The saved report this file is a run of; stored with it so the Reporting page lists it there. */
    definitionId?: string
  }) => api.post('/reports/generate', params, { responseType: 'blob' }),
  /**
   * The exact artifact, for the editor's preview pane. Same builders and brand
   * guidelines as generate; records no generation and spends no export
   * approval, so a designer nudging a colour does not fill the delivery
   * history or burn a one-time token.
   */
  preview: (params: {
    template: string
    filters?: Record<string, unknown>
    /** Unsaved brand guidelines to render inside — the guidelines screen's draft. */
    branding?: Record<string, unknown>
  }) => api.post('/reports/preview', params, { responseType: 'blob' }),
  /**
   * The dossier's section catalogue for ONE deal, with per-deal availability.
   * Availability is why this is fetched rather than hardcoded: a section the
   * deal has no content for is offered as empty, not silently dropped.
   */
  dealPackageOptions: (loanId: string, documentKind: 'book' | 'package' = 'book') =>
    api
      .get<{
        sections: Array<{ id: string; label: string; available: boolean }>
      }>('/reports/deal-package/options', {
        params: { loan_id: loanId, document_kind: documentKind },
      })
      .then((r) => r.data),
  generateInternal: (params: object) =>
    api.post('/reports/internal', params, { responseType: 'blob' }),
  generateInvestor: (params: object) =>
    api.post('/reports/investor', params, { responseType: 'blob' }),
  getScheduled: () =>
    api.get('/reports/scheduled').then((r) => r.data),
}
